import React, { useEffect, useState } from "react";
import { faExpand, faEye, faTimes } from "@fortawesome/free-solid-svg-icons";

import Badge from "../../components/Badge";
import Button from "../../components/Button";
import Collaborators from "../../components/Collaborators";
import ComingSoon from "../../components/ComingSoon";
import CommentsSection from "../../components/CommentsSection";
import Container from "../../components/Container";
import ContainerDivider from "../../components/ContainerDivider";
import Divider from "../../components/Divider";
import Footer from "../../components/Footer";
import Icon from "../../components/Icon";
import InteractiveWidget from "../../components/InteractiveWidget";
import Navbar from "../../components/Navbar";
import NextSessionsWidget from "../../components/NextSessionsWidget";
import ProfileBanner from "../../components/ProfileBanner";
import Ratio from "../../components/Ratio";
import RelatedPostsWidget from "../../components/RelatedPostsWidget";
import Spacer from "../../components/Spacer";
import Spinner from "../../components/Spinner";
import SubscriptionSection from "../../components/SubscriptionSection";
import VideoPlayer from "../../components/VideoPlayer";
import ViewersWidget from "../../components/ViewersWidget";
import addComment from "../../helpers/addComment";
import addViewer from "../../helpers/addViewer";
import getTime from "../../helpers/getTime";
import removeViewer from "../../helpers/removeViewer";
import useArchivedSessions from "../../hooks/useArchivedSessions";
import useComments from "../../hooks/useComments";
import useCurrentUser from "../../hooks/useCurrentUser";
import useEvent from "../../hooks/useEvent";
import useFakeView from "../../hooks/useFakeView";
import useLiveSession from "../../hooks/useLiveSession";
import { useParams } from "react-router-dom";
import useRoom from "../../hooks/useRoom";
import useSession from "../../hooks/useSession";

export interface SessionProps {
  ID: string;
  src?: string;
  title: string;
  image?: string;
  description?: string;
  timeStart: Date;
  timeEnd: Date;
}

export interface RelatedSessionProps {
  title: string;
  subtitle: string;
  link: string;
  image?: string;
  cover?: string;
}

export interface SpeakerProps {
  firstname: string;
  lastname: string;
  title?: string;
  link: string;
  bio?: string;
  position?: string;
  institute?: string;
  email?: string;
}

export interface ViewerProps {
  ID: string;
  image?: string;
}

export interface RoomProps {
  src?: string;
  transitionImage?: string;
  open: boolean;
  forceOpen: boolean;
  speaker?: SpeakerProps;
}

const Video = () => {
  const { roomID, sessionID } = useParams<{
    roomID?: string;
    sessionID?: string;
  }>();

  const currentUser = useCurrentUser();
  const fakeView = useFakeView();
  const event = useEvent();
  const sessions = useArchivedSessions();
  const archivedSession = useSession(sessionID);
  const liveSession = useLiveSession(roomID ? parseInt(roomID) : undefined);
  const currentRoom = useRoom(roomID ? parseInt(roomID) : undefined);

  const [isLoading, setIsLoading] = useState(true);
  const [pageTitle, setPageTitle] = useState("Page Title");
  const [pageHeader, setPageHeader] = useState("Page Header");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [session, setSession] = useState<SessionProps>();
  const [speaker, setSpeaker] = useState<SpeakerProps>();
  const [viewers, setViewers] = useState<ViewerProps[]>();
  const [room, setRoom] = useState<RoomProps>();
  const [relatedSessions, setRelatedSessions] = useState<
    RelatedSessionProps[]
  >();

  // Get comments
  const comments = useComments("sessions", session?.ID);

  // Remove loading flag
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Set current room
  useEffect(() => {
    if (currentRoom) {
      setRoom(currentRoom);
    }
  }, [currentRoom]);

  // Set live session
  useEffect(() => {
    if (roomID && liveSession) {
      setPageHeader("Live Session");
      setPageTitle(`Virtual Room ${roomID}`);
      setSession(liveSession);

      if (liveSession.speaker) {
        setSpeaker(liveSession.speaker);
      }

      if (liveSession.viewers) {
        setViewers(liveSession.viewers);
      }
    }
  }, [roomID, liveSession]);

  // Set archived session
  useEffect(() => {
    if (sessionID && archivedSession) {
      setPageHeader("Archived Session");
      setPageTitle("Recorded Video");
      setSession(archivedSession);

      if (archivedSession.speaker) {
        setSpeaker(archivedSession.speaker);
      }
    }
  }, [sessionID, archivedSession]);

  // Set related sessions
  useEffect(() => {
    if (sessionID && sessions) {
      setRelatedSessions(
        sessions.filter((session: any) => session.ID !== sessionID)
      );
    }
  }, [sessionID, sessions, event]);

  // Add / remove user to viewers list
  useEffect(() => {
    if (currentUser && session) {
      const userID = currentUser.ID;
      const sessionID = session.ID;

      if (userID && sessionID) {
        addViewer(userID, sessionID);
      }

      return () => {
        removeViewer(userID, session.ID);
      };
    }
  }, [currentUser, session]);

  // Handle new comment
  const handleNewComment = (comment: string) => {
    if (currentUser && session) {
      const userID = currentUser.ID;
      const sessionID = session.ID;

      if (userID && sessionID) {
        addComment(userID, comment, "sessions", sessionID);
      }
    }
  };

  // Check for infinite loop
  console.log("Re-render");

  return (
    <div className="VideoPage">
      <Navbar />

      {isLoading && !session ? (
        <Container>
          <div className="w-full p-4 my-2 text-sm text-center text-gray-400 bg-gray-100 rounded">
            <div className="flex items-center justify-center space-x-4">
              <span>
                <Spinner />
              </span>{" "}
              <span>Loading...</span>
            </div>
          </div>
        </Container>
      ) : !session ? (
        <ComingSoon pageName={`Virtual Room ${roomID}`} />
      ) : isFullscreen ? (
        <div className="fixed top-0 left-0 z-10 w-full h-full">
          <div className="w-full h-full bg-gray-100 lg:absolute lg:top-0 lg:left-0">
            <div className="w-full h-full lg:flex lg:flex-wrap">
              <div className="w-full h-full lg:w-3/12">
                <div className="relative w-full h-full">
                  <div className="p-4 lg:absolute lg:top-0 lg:right-0 lg:z-10">
                    <button
                      type="button"
                      onClick={() => setIsFullscreen(false)}
                    >
                      <Icon icon={faTimes} />
                    </button>
                  </div>
                  <div className="w-full h-full lg:absolute lg:top-0 lg:left-0 lg:z-0">
                    <InteractiveWidget
                      session={{
                        title: session?.title || "",
                        description: session?.description || "",
                        timeStart: session?.timeStart || getTime(),
                        timeEnd: session?.timeEnd || getTime(),
                      }}
                      speaker={speaker}
                      comments={comments}
                      onNewComment={handleNewComment}
                    />
                  </div>
                </div>
              </div>
              <div className="w-full h-full bg-black lg:w-9/12">
                <div className="relative flex items-center justify-center w-full h-full">
                  {viewers ? (
                    <div className="absolute top-0 right-0 z-10 m-4">
                      <Badge
                        text={
                          <>
                            <Icon icon={faEye} /> {viewers.length + fakeView}
                          </>
                        }
                      />
                    </div>
                  ) : null}
                  <Ratio w={4} h={3}>
                    {room?.transitionImage ? (
                      <div
                        className="w-full h-full bg-black bg-center bg-no-repeat bg-contain"
                        style={{
                          backgroundImage: `url(${room.transitionImage})`,
                        }}
                      ></div>
                    ) : (
                      <VideoPlayer
                        title={session?.title || ""}
                        src={session?.src || room?.src || ""}
                        thumbnail={session?.image || ""}
                      />
                    )}
                  </Ratio>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <Container>
            <div className="my-4 text-lg font-medium text-aqua">
              {pageHeader}
            </div>
            <div className="my-4 text-5xl font-bold leading-none">
              {pageTitle}
            </div>
          </Container>

          <Container>
            <div className="space-y-12">
              <div className="w-full">
                <div className="flex flex-wrap mt-8 lg:flex-no-wrap">
                  <div className="w-full lg:w-4/12">
                    <InteractiveWidget
                      session={{
                        title: session?.title || "",
                        description: session?.description || "",
                        timeStart: session?.timeStart || getTime(),
                        timeEnd: session?.timeEnd || getTime(),
                      }}
                      speaker={speaker}
                      comments={comments}
                      onNewComment={handleNewComment}
                    />
                  </div>
                  <div className="relative w-full lg:w-8/12">
                    {viewers ? (
                      <div className="absolute top-0 right-0 z-10 m-4">
                        <Badge
                          text={
                            <>
                              <Icon icon={faEye} /> {viewers.length + fakeView}
                            </>
                          }
                        />
                      </div>
                    ) : null}
                    <Ratio w={4} h={3}>
                      {room?.transitionImage ? (
                        <div
                          className="w-full h-full bg-black bg-center bg-no-repeat bg-contain"
                          style={{
                            backgroundImage: `url(${room.transitionImage})`,
                          }}
                        ></div>
                      ) : (
                        <VideoPlayer
                          title={session?.title || ""}
                          src={session?.src || room?.src || ""}
                          thumbnail={session?.image || ""}
                        />
                      )}
                    </Ratio>
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    type="button"
                    theme="secondary"
                    onClick={() => setIsFullscreen(true)}
                  >
                    <Icon icon={faExpand} /> Enter Fullscreen Mode
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap space-y-8 lg:space-y-0 lg:flex-no-wrap">
                <div className="w-full lg:w-9/12 lg:pr-8">
                  <CommentsSection
                    comments={comments}
                    onNewComment={handleNewComment}
                  />
                </div>
                <div className="w-full lg:w-3/12">
                  <div className="space-y-8">
                    {relatedSessions ? (
                      <RelatedPostsWidget items={relatedSessions} />
                    ) : (
                      <>
                        <ViewersWidget viewers={viewers} />
                        <Divider />
                        <NextSessionsWidget />
                      </>
                    )}
                  </div>
                </div>
              </div>

              {speaker ? (
                <ProfileBanner
                  title={speaker.title || ""}
                  firstname={speaker.firstname || ""}
                  lastname={speaker.lastname || ""}
                  position={speaker.position || ""}
                  institute={speaker.institute || ""}
                  link={speaker.link || ""}
                  email={speaker.email || ""}
                />
              ) : null}
            </div>
          </Container>
        </>
      )}

      <Spacer>
        <ContainerDivider />
        <Collaborators />
      </Spacer>

      <SubscriptionSection />
      <Footer />
    </div>
  );
};

export default Video;
